/**
 * usePersistence - Cookie による永続化
 */
import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useDashboard } from '../contexts/DashboardContext';
import { isValidActionForPage } from '../config/actions';

const COOKIE_DAYS = 30;

/**
 * Cookie を設定
 */
function setCookie(name, value, days) {
  const date = new Date();
  date.setTime(date.getTime() + days * 24 * 60 * 60 * 1000);
  document.cookie = `${name}=${value};expires=${date.toUTCString()};path=/`;
}

/**
 * Cookie を取得
 */
function getCookie(name) {
  const cookies = document.cookie.split(';');
  for (let cookie of cookies) {
    const [key, value] = cookie.trim().split('=');
    if (key === name) return value;
  }
  return '';
}

/**
 * ページタイプに応じたCookieキーを取得
 */
function getCookieKeys(pageType) {
  const prefix = pageType === 'purpose' ? 'purpose_' : 
                pageType === 'function' ? 'function_' : '';
  
  return {
    LOCATION: `${prefix}dashboard_location`,
    ACTION: `${prefix}dashboard_action`,
    YEAR: `${prefix}dashboard_year`,
    MONTH: `${prefix}dashboard_month`
  };
}

/**
 * Cookie による永続化フック
 */
export function usePersistence() {
  const routeLocation = useLocation();
  const { 
    location, action, year, month, 
    setLocation, setAction, setYear, setMonth 
  } = useDashboard();
  
  // ページタイプ判定
  const pageType = routeLocation.pathname.includes('/function') ? 'function' : 
                   routeLocation.pathname.includes('/purpose') || routeLocation.pathname === '/' ? 'purpose' : 
                   'default';
  const cookieKeys = getCookieKeys(pageType);
  
  // 初回: Cookieから状態を復元（URLパラメータがない場合のみ）
  useEffect(() => {
    // URLパラメータが優先されるため、値がない場合のみCookieから復元
    if (!location) {
      const savedLocation = getCookie(cookieKeys.LOCATION);
      if (savedLocation) setLocation(savedLocation);
    }

    if (!action) {
      const savedAction = getCookie(cookieKeys.ACTION);
      // 旧 prediction_calendar など、現在のページで無効なアクションは無視する
      if (
        savedAction
        && (pageType === 'default' || isValidActionForPage(savedAction, pageType))
      ) {
        setAction(savedAction);
      } else if (savedAction) {
        // 無効な値が Cookie に残っている場合は失効させる
        setCookie(cookieKeys.ACTION, '', 0);
      }
    }
    
    if (!year || year === new Date().getFullYear().toString()) {
      const savedYear = getCookie(cookieKeys.YEAR);
      if (savedYear) setYear(savedYear);
    }
    
    if (!month || month === (new Date().getMonth() + 1).toString()) {
      const savedMonth = getCookie(cookieKeys.MONTH);
      if (savedMonth) setMonth(savedMonth);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // 現在のページで使えなくなった action（旧 prediction_calendar など）は
  // 現在の URL/Cookie から検出した時点でクリアする。
  useEffect(() => {
    if (!action) return;
    if (pageType === 'default') return;
    if (!isValidActionForPage(action, pageType)) {
      setAction('');
      setCookie(cookieKeys.ACTION, '', 0);
    }
  }, [action, pageType, cookieKeys.ACTION, setAction]);
  
  // 状態変更時: Cookieに保存
  useEffect(() => {
    if (location) setCookie(cookieKeys.LOCATION, location, COOKIE_DAYS);
  }, [location, cookieKeys.LOCATION]);
  
  useEffect(() => {
    if (action) setCookie(cookieKeys.ACTION, action, COOKIE_DAYS);
  }, [action, cookieKeys.ACTION]);
  
  useEffect(() => {
    if (year) setCookie(cookieKeys.YEAR, year, COOKIE_DAYS);
  }, [year, cookieKeys.YEAR]);
  
  useEffect(() => {
    if (month) setCookie(cookieKeys.MONTH, month, COOKIE_DAYS);
  }, [month, cookieKeys.MONTH]);
}
